import { useState } from "react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink, MapPin, Check } from "lucide-react";
import { toast } from "sonner";

// Fix default Leaflet marker icons (webpack breaks their default resolution)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export default function MiniMap({ lat, lng, label, testId }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (lat == null || lng == null) return null;

  const position = [lat, lng];
  const googleUrl = `https://www.google.com/maps?q=${lat},${lng}`;

  const copyGoogleUrl = async () => {
    // Try to open Google Maps directly (works in normal browsers; blocked only inside sandboxed iframes)
    let win = null;
    try { win = window.open(googleUrl, "_blank", "noopener,noreferrer"); } catch { win = null; }
    // Also copy to clipboard as a safety net for restrictive environments
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(googleUrl);
      } else {
        const ta = document.createElement("textarea");
        ta.value = googleUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch { /* ignore */ }
        document.body.removeChild(ta);
      }
      setCopied(true);
      if (win) {
        toast.success("Abriendo Google Maps… (URL también copiada al portapapeles)");
      } else {
        toast.success("URL de Google Maps copiada. Pégala en una pestaña nueva del navegador.");
      }
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar. Selecciona la URL manualmente y cópiala.");
    }
  };

  return (
    <>
      <div
        className="relative flex-shrink-0"
        style={{ isolation: "isolate", zIndex: 0 }}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={testId}
          aria-label={label ? `Ver mapa: ${label}` : "Ver mapa"}
          className="group block w-24 h-16 rounded-lg overflow-hidden border border-slate-200 hover:border-[#0E78C2] transition-colors relative cursor-pointer"
        >
          <MapContainer
            center={position}
            zoom={15}
            style={{ height: "100%", width: "100%" }}
            zoomControl={false}
            dragging={false}
            scrollWheelZoom={false}
            doubleClickZoom={false}
            touchZoom={false}
            keyboard={false}
            attributionControl={false}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={position} />
          </MapContainer>
          {/* Overlay to make the whole tile clickable (blocks Leaflet interactions) */}
          <div className="absolute inset-0 z-[500] bg-transparent group-hover:bg-[#0E78C2]/10 transition-colors" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl p-0 overflow-hidden z-[900]"
          data-testid={testId ? `${testId}-dialog` : undefined}
        >
          <div className="p-5 border-b border-slate-200 flex flex-wrap items-start justify-between gap-3 pr-14">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                {label || "Ubicación"}
              </div>
              <DialogTitle asChild>
                <div className="font-display font-extrabold text-lg text-[#0B2A4A] leading-tight mt-0.5 inline-flex items-center gap-1.5">
                  <MapPin size={16} className="text-[#F5A623]" /> {lat.toFixed(6)}, {lng.toFixed(6)}
                </div>
              </DialogTitle>
            </div>
            <button
              type="button"
              onClick={copyGoogleUrl}
              data-testid={testId ? `${testId}-open-google` : "copy-google-maps"}
              className={`inline-flex items-center gap-2 text-xs whitespace-nowrap px-4 h-10 rounded-lg font-semibold transition-colors ${
                copied
                  ? "bg-emerald-600 text-white"
                  : "bg-gradient-to-br from-[#FF8A1F] to-[#F5A623] text-white hover:shadow-lg"
              }`}
            >
              {copied ? <><Check size={14} /> Abriendo…</> : <><ExternalLink size={14} /> Abrir en Google Maps</>}
            </button>
          </div>
          <div
            className="px-5 py-2 border-b border-slate-100 bg-slate-50 text-[11px] font-mono text-slate-600 select-all break-all"
            data-testid={testId ? `${testId}-url` : undefined}
          >
            {googleUrl}
          </div>
          <div className="relative" style={{ isolation: "isolate", zIndex: 0 }}>
            <div className="h-[420px] w-full">
              <MapContainer
                center={position}
                zoom={17}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <Marker position={position} />
              </MapContainer>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
